import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast, { Toaster } from "react-hot-toast";

import clsx from "clsx";

import {
  actionGetUsersSelect,
  actionGetUsers,
} from "../../../redux/action/easymerch/actionUsers";
import {
  reducerSelectedContracts,
  reducerUsersInputValues,
  reducerUsersStatus,
} from "../../../redux/reducer/easymerch/reducers/reducerUsers";
import { generateStyles } from "../../../utils/generateToastStyles";
import { AppDispatch } from "../../../redux/reducer/store";

import LimitSelect from "../../custom/limitSelect/limitSelect";
import ToastPopup from "../../custom/toastPopup/toastPopup";
import Pagination from "../../pagination/pagination";
import Select from "../../custom/select/select";
import Loader from "../../custom/loader/loader";

import "../table.scss";
import { IUserOptions } from "../../../types/interfaces";

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

interface IUser {
  emData: {
    dateFire: string;
    dateHire: string;
    emId: number;
    fullName: string;
    personCol1: number;
    personId: number;
    userId: number;
    userLogin: string;
    userPassword: string;
    usergroupName: string;
  };
  enabled: boolean;
  fullname: string;
  id: number;
  idMol: number;
  login: string;
  role: { [key: string]: string };
}

const requestStatusOptions = [
    {
        status: 200,
        successMessage: "Маршрут отправлен в EasyMerch!",
    },
    {
        status: 406,
        errorMessage: 'Визиты за заданный период отсутствуют! Запустите отправку визитов в другой день, или установите визиты в заданный период.',
    },
    {
        status: 503,
        errorMessage: 'Ошибка подключения к EasyMerch! Обратитесь к администратору.',
    },
    {
        status: 0,
        errorMessage: 'Ошибка! Маршрут не отправлен в EasyMerch.',
    },
    {
        status: "emptyList",
        errorMessage: "Выделите хотя бы одну запись в таблице!",
    },
];

const TableUsers: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    usersRequestStatus,
    usersInputValues,
    selectedUsers,
    rolesOptions,
    usersCount,
    offset,
    users,
    limit,
  } = useSelector((state: any) => state.easymerch.users);
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );
  const [isInputChange, setIsInputChange] = useState<boolean>(false);
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);

  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || limit;

  useEffect(() => {
    setIsPending(true);
    dispatch(actionGetUsers(actualLimit, offset)).then(() => {
      setIsPending(false);
    });
    dispatch(actionGetUsersSelect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedUsers && !selectedUsers.length) {
      setCheckedRows([]);
    }
  }, [selectedUsers]);

  useEffect(() => {
    requestStatusOptions.forEach(
      (option: {
        status: number | string;
        successMessage?: string;
        errorMessage?: string;
      }) => {

        if (usersRequestStatus === option.status) {
          toast.dismiss();

          toast(
            (t) => (
              <ToastPopup
                title={
                  option.successMessage
                    ? option.successMessage
                    : option.errorMessage
                }
                isSuccess={!!option.successMessage}
                toast={toast}
                t={t}
              />
            ),
            generateStyles(!!option.successMessage)
          );
        }
      }
    );

    setTimeout(() => {
      dispatch(reducerUsersStatus(null));
    }, 10000);
  }, [dispatch, usersRequestStatus]);
  const selectRolesOptions = rolesOptions && [
    { value: "", label: "Не указано" },
    ...rolesOptions,
  ];

  useEffect(() => {
    const handleDocumentClick = () => {
      setIsPending(true);
      dispatch(actionGetUsers(limit, offset)).then(() => {
        setIsPending(false);
      });
      setIsInputChange(false);
    };

    if (isInputChange) {
      document.addEventListener("click", handleDocumentClick);

      return () => {
        document.removeEventListener("click", handleDocumentClick);
      };
    } else
      return () => {
        document.removeEventListener("click", handleDocumentClick);
      };
  }, [isInputChange, dispatch, limit, offset]);

  const handleRowCheckboxChange = (id: number) => {
    if (checkedRows.includes(id)) {
      setCheckedRows((prevCheckedRows: number[]) =>
        prevCheckedRows.filter((row: number) => row !== id)
      );

      const filteredSelectedUsers =
        users &&
        users
          ?.slice()
          ?.filter((user: IUser) => checkedRows?.includes(user.emData.emId))
          ?.filter((row: IUser) => row.emData.emId !== id)
          ?.map((elem: IUser) => elem.idMol);

      dispatch(reducerSelectedContracts(filteredSelectedUsers));
    } else {
      setCheckedRows((prevCheckedRows: number[]) => [...prevCheckedRows, id]);

      const filteredSelectedUsers =
        users &&
        users
          ?.slice()
          ?.filter((user: IUser) => checkedRows?.includes(user.emData.emId))
          ?.map((elem: IUser) => elem.idMol);

      const elementToAdd =
        users && users.find((elem: IUser) => elem.emData.emId === id).idMol;

      dispatch(
        reducerSelectedContracts([...filteredSelectedUsers, elementToAdd])
      );
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    dispatch(reducerUsersInputValues({ ...usersInputValues, [name]: value }));
    setIsInputChange(true);
  };

  const selectChange = (event: { value: string; label: string }) => {
    if (event.value === "") {
      const updatedInputValues = { ...usersInputValues };
      delete updatedInputValues.role;

      dispatch(reducerUsersInputValues(updatedInputValues));
    } else {
      dispatch(
        reducerUsersInputValues({ ...usersInputValues, role: event.value })
      );
    }

    setIsPending(true);
    dispatch(actionGetUsers(limit, offset)).then(() => {
      setIsPending(false);
    });
    setIsInputChange(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsPending(true);
      dispatch(actionGetUsers(limit, offset)).then(() => {
        setIsPending(false);
      });
      setIsInputChange(false);
    }
  };

  const handleChange = (newLimit: number, newOffset: number) => {
    setIsPending(true);
    dispatch(actionGetUsers(newLimit, newOffset)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem("pageLimit", JSON.stringify(newLimit));
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 0);
    setCurrentPage(1);
    setStartPage(1);
  };

  return (
    <>
      <div className="loader__wrapper">
        {(!users || isPending) && <Loader />}
      </div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            "active__table",
          !isOpenSidebar && "closed__table",
          "table"
        )}
      >
        {usersRequestStatus && usersRequestStatus !== null && (
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 5000,
            }}
          />
        )}
        <div className="filters__container__mobile">
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                value={usersInputValues.idMol}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="idMol"
                type="text"
                id="idMoll"
              />
              <label>ID MOL</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                value={usersInputValues.login}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="login"
                type="text"
                id="login"
              />
              <label>Логин</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                value={usersInputValues.fullname}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="fullname"
                id="fullname"
                type="text"
              />
              <label>ФИО</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  usersInputValues.role &&
                  selectRolesOptions.find(
                    (option: { value: number; label: string }) =>
                      option.value === usersInputValues.role
                  )
                }
                isShowPlaceholder={
                  !!usersInputValues.role || usersInputValues.role === undefined
                }
                options={selectRolesOptions}
                propsChange={selectChange}
                className="users__filter"
                placeholder="Должность"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                value={usersInputValues.emPersonId}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                name="emPersonId"
                placeholder=" "
                id="emPersonId"
                type="text"
              />
              <label>ID физ. лица</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                value={usersInputValues.emId}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="emId"
                type="text"
                id="emId"
              />
              <label>ID EM</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                value={usersInputValues.emLogin}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                id="emUserLogin"
                placeholder=" "
                name="emLogin"
                type="text"
              />
              <label>Логин EM</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                value={usersInputValues.emName}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                id="emFullName"
                name="emName"
                type="text"
              />
              <label>Имя EM</label>
            </div>
          </div>
        </div>
        <div className="table__wrapper">
          <table>
            <thead className="table__header__filters">
              <tr>
                <th className="reports__checkbox_block"></th>
                <th>
                  <div className="filter__block">
                    <input
                      value={usersInputValues.idMol}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="idMol"
                      type="text"
                      id="idMoll"
                    />
                    <label>ID MOL</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      value={usersInputValues.login}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="login"
                      type="text"
                      id="login"
                    />
                    <label>Логин</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      value={usersInputValues.fullname}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="fullname"
                      id="fullname"
                      type="text"
                    />
                    <label>ФИО</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        usersInputValues.role &&
                        selectRolesOptions.find(
                          (option: { value: number; label: string }) =>
                            option.value === usersInputValues.role
                        )
                      }
                      isShowPlaceholder={
                        !!usersInputValues.role ||
                        usersInputValues.role === undefined
                      }
                      options={selectRolesOptions}
                      propsChange={selectChange}
                      className="users__filter"
                      placeholder="Должность"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      value={usersInputValues.emPersonId}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      name="emPersonId"
                      placeholder=" "
                      id="emPersonId"
                      type="text"
                    />
                    <label>ID физ. лица</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      value={usersInputValues.emId}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="emId"
                      type="text"
                      id="emId"
                    />
                    <label>ID EM</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      value={usersInputValues.emLogin}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      id="emUserLogin"
                      placeholder=" "
                      name="emLogin"
                      type="text"
                    />
                    <label>Логин EM</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      value={usersInputValues.emName}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      id="emFullName"
                      name="emName"
                      type="text"
                    />
                    <label>Имя EM</label>
                  </div>
                </th>
                <th>
                  <span className="filter__title">Начало действия</span>
                </th>
                <th>
                  <span className="filter__title">Окончание действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users &&
                users.map((report: IUser) => {
                  return (
                    <tr
                      className={!report.enabled ? "active__tr" : ""}
                      key={report.id}
                    >
                      <td>
                        {report.enabled && (
                          <input
                            onChange={() =>
                              handleRowCheckboxChange(report.emData.emId)
                            }
                            checked={
                              (checkedRows &&
                                checkedRows.includes(report.emData.emId)) ||
                              false
                            }
                            type="checkbox"
                          />
                        )}
                      </td>
                      <td>{report.idMol}</td>
                      <td>{report.login}</td>
                      <td>{report.fullname}</td>
                      <td>{Object.values(report.role)}</td>
                      <td>{report.emData.personId}</td>
                      <td>{report.emData.emId}</td>
                      <td>{report.emData.userLogin}</td>
                      <td>{report.emData.fullName}</td>
                      <td>
                        {report.emData.dateHire
                          ?.split("-")
                          ?.reverse()
                          ?.join(".")}
                      </td>
                      <td>
                        {report.emData.dateFire
                          ?.split("-")
                          ?.reverse()
                          ?.join(".")}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination__count">Всего: {usersCount}</div>
          <div className="pagination__str">
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            setStartPage={setStartPage}
            handleChange={handleChange}
            currentPage={currentPage}
            startPage={startPage}
            limit={actualLimit}
            count={usersCount}
          />
        </div>
      </div>
    </>
  );
};

export default TableUsers;
